import OrderForwardListParams, {
  OrderForwardListParamsOrder,
} from "../../../../../../common/services/order-forward/list/order-forward-list-params";
import paginationService from "../../../../../../common/utils/pagination/pagination.service";
import BillingsTaxiUnbilledTransferredOrderListingFilter, {
  BillingsTaxiUnbilledTransferredOrderListingProducerTaxiFilter,
  BillingsTaxiUnbilledTransferredOrderListingDriverFilter,
  BillingsTaxiUnbilledTransferredOrderListingExternalOrderIdFilter,
  BillingsTaxiUnbilledTransferredOrderListingInternalOrderIdFilter,
  BillingsTaxiUnbilledTransferredOrderListingPassengerFilter,
  BillingsTaxiUnbilledTransferredOrderListingRouteAddressFilter,
  BillingsTaxiUnbilledTransferredOrderListingRouteDestinationAddressFilter,
  BillingsTaxiUnbilledTransferredOrderListingRouteIntermediateAddressFilter,
  BillingsTaxiUnbilledTransferredOrderListingRoutePickupAddressFilter,
  BillingsTaxiUnbilledTransferredOrderListingStartDateFilter,
  BillingsTaxiUnbilledTransferredOrderListingConsumerTaxiFilter,
} from "../types/billings-taxi-unbilled-transferred-order-listing-filter";
import BillingsTaxiUnbilledTransferredOrderListingFilterType from "../types/billings-taxi-unbilled-transferred-order-listing-filter-type";
import BillingsTaxiUnbilledTransferredOrderListingSortKey from "../types/billings-taxi-unbilled-transferred-order-listing-sort-key";

const createRequestOrder = (
  sortKey: BillingsTaxiUnbilledTransferredOrderListingSortKey | null,
): OrderForwardListParamsOrder | undefined => {
  const options: {
    sortKey: BillingsTaxiUnbilledTransferredOrderListingSortKey;
    requestOrder: OrderForwardListParamsOrder;
  }[] = [
    {
      requestOrder: OrderForwardListParamsOrder.CONSUMER_ASC,
      sortKey: BillingsTaxiUnbilledTransferredOrderListingSortKey.CONSUMER_ASC,
    },
    {
      requestOrder: OrderForwardListParamsOrder.CONSUMER_DESC,
      sortKey: BillingsTaxiUnbilledTransferredOrderListingSortKey.CONSUMER_DESC,
    },
    {
      requestOrder: OrderForwardListParamsOrder.PRODUCER_ASC,
      sortKey: BillingsTaxiUnbilledTransferredOrderListingSortKey.PRODUCER_ASC,
    },
    {
      requestOrder: OrderForwardListParamsOrder.PRODUCER_DESC,
      sortKey: BillingsTaxiUnbilledTransferredOrderListingSortKey.PRODUCER_DESC,
    },
  ];

  return options.find((option) => option.sortKey === sortKey)?.requestOrder;
};

const createParams = (
  page: number,
  pageSize: number,
  filters: BillingsTaxiUnbilledTransferredOrderListingFilter[],
  sortKey: BillingsTaxiUnbilledTransferredOrderListingSortKey | null,
): OrderForwardListParams => {
  const offset = paginationService.calculateOffset(page, pageSize);

  return {
    order: createRequestOrder(sortKey),
    address: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledTransferredOrderListingFilterType.ROUTE_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledTransferredOrderListingRouteAddressFilter["value"]
      | undefined,
    limit: pageSize,
    producer: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledTransferredOrderListingFilterType.PRODUCER_TAXI,
    )?.value as
      | BillingsTaxiUnbilledTransferredOrderListingProducerTaxiFilter["value"]
      | undefined,
    consumer: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledTransferredOrderListingFilterType.CONSUMER_TAXI,
    )?.value as
      | BillingsTaxiUnbilledTransferredOrderListingConsumerTaxiFilter["value"]
      | undefined,
    driver: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledTransferredOrderListingFilterType.DRIVER,
    )?.value as
      | BillingsTaxiUnbilledTransferredOrderListingDriverFilter["value"]
      | undefined,
    endAddress: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledTransferredOrderListingFilterType.ROUTE_DESTINATION_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledTransferredOrderListingRouteDestinationAddressFilter["value"]
      | undefined,
    externalId: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledTransferredOrderListingFilterType.EXTERNAL_ORDER_ID,
    )?.value as
      | BillingsTaxiUnbilledTransferredOrderListingExternalOrderIdFilter["value"]
      | undefined,
    humanId: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledTransferredOrderListingFilterType.INTERNAL_ORDER_ID,
    )?.value as
      | BillingsTaxiUnbilledTransferredOrderListingInternalOrderIdFilter["value"]
      | undefined,
    midAddress: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledTransferredOrderListingFilterType.ROUTE_INTERMEDIATE_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledTransferredOrderListingRouteIntermediateAddressFilter["value"]
      | undefined,
    offset,
    passenger: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledTransferredOrderListingFilterType.PASSENGER,
    )?.value as
      | BillingsTaxiUnbilledTransferredOrderListingPassengerFilter["value"]
      | undefined,
    startAddress: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledTransferredOrderListingFilterType.ROUTE_PICKUP_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledTransferredOrderListingRoutePickupAddressFilter["value"]
      | undefined,
    startTimeSince: (
      filters.find(
        (filter) =>
          filter.type ===
          BillingsTaxiUnbilledTransferredOrderListingFilterType.START_DATE,
      )?.value as
        | BillingsTaxiUnbilledTransferredOrderListingStartDateFilter["value"]
        | undefined
    )?.from?.toJSON(),
    startTimeTo: (
      filters.find(
        (filter) =>
          filter.type ===
          BillingsTaxiUnbilledTransferredOrderListingFilterType.START_DATE,
      )?.value as
        | BillingsTaxiUnbilledTransferredOrderListingStartDateFilter["value"]
        | undefined
    )?.to?.toJSON(),
  };
};

const billingsTaxiUnbilledTransferredOrderListingRequestFactory = {
  createParams,
};

export default billingsTaxiUnbilledTransferredOrderListingRequestFactory;
